import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, matchPath, useLocation } from "react-router-dom";
import { Breadcrumb, Select, Form, InputNumber, message } from "antd";

import { useGetShopProductByIdQuery } from "graphql/queries/get-shop-product-by-id.generated";
import { useGetProductForOrderQuery } from "graphql/queries/get-product-for-order.generated";
import { useGetInstalledDevicesByDeviceIdQuery } from "graphql/queries/get-installed-devices-by-device-id.generated";
import { User_Properties, Properties, Products } from "graphql/generated/graphql";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useCartStatus } from "state/reactive-vars/cart-status-var";
import { useAiChatStatus, aiChatStatusInitialValue } from "state/reactive-vars/ai-chat-status-var";

import MediaPlayer from "components/media-player/media-player";
import { AiChatOrigin, MediaType } from "constants/field-values";
import routeNames from "constants/route-names";
import { useOrderFiltersStore } from "state/zustand-stores/order-filters-store";
import DeviceAttachment from "components/device-attachment/device-attachment";

const ShopProductDetails: React.FC = () => {
	const setCategoryFilters = useOrderFiltersStore(state => state.setCategoryFilters);
	const { loggedUserValue } = useLoggedUser();
	const { addToCart } = useCartStatus();
	const { updateAiChatStatus } = useAiChatStatus();
	const params = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { pathname } = location;
	const match = matchPath(routeNames.ADMIN_SHOP_PRODUCTS_DETAILS, pathname);
	const isAdmin = match !== null;
	const [fileViewer, setFileViewer] = useState<{
		type: MediaType | null;
		src: string;
	}>({ type: null, src: "" });
	const [isLoadingAttachment, setIsLoadingAttachment] = useState<boolean>(false);
	const [userPropertiesIds, setUserPropertiesIds] = useState<Array<string>>([]);
	const [propertiesOptions, setPropertiesOptions] = useState<Array<any>>([]);
	const [selectedPropertyKey, setSelectedPropertyKey] = useState<string>("");
	const [selectedProperty, setSelectedProperty] = useState<Properties | null>(null);
	const [quantity, setQuantity] = useState<number | null>(null);
	const [defaultShopProductId, setDefaultShopProductId] = useState<string>("");

	const handleOnOpenPDF = useCallback(async (attachment: any) => {
		setIsLoadingAttachment(true);
		if (attachment.document_link) {
			try {
				setFileViewer({
					type: MediaType.Document,
					src: attachment.document_link
				});
				setIsLoadingAttachment(false);
			} catch (error) {
				console.log("handleOnOpenPDF error: ", error);
			}
		}
	}, []);

	const handleOnOpenVideo = useCallback((attachment: any) => {
		if (attachment.video_link) {
			setFileViewer({ type: MediaType.Video, src: attachment.video_link });
		}

		if (attachment.photo_link) {
			setFileViewer({ type: MediaType.Image, src: attachment.photo_link });
		}
	}, []);

	useEffect(() => {
		if (loggedUserValue && loggedUserValue.user_properties && loggedUserValue.user_properties.length) {
			const loggedUserPropertiesIds = loggedUserValue.user_properties.map(
				(userprop: User_Properties) => userprop.property_id
			);

			setUserPropertiesIds(loggedUserPropertiesIds);
		}
	}, [loggedUserValue]);

	const handleOnCompleteGetProductForOrder = useCallback((data: any) => {
		// console.log("Complete Get Product For Order data: ", data);

		if (data && data?.products_by_pk) {
			const { shop_products } = data?.products_by_pk;

			if (shop_products && shop_products.length) {
				setDefaultShopProductId(shop_products[0].id);

				const propertyOptionsValues = shop_products.map(element => {
					return {
						value: element.property?.id,
						label: element.property?.name,
						property: element.property
					};
				});

				setPropertiesOptions(propertyOptionsValues);

				if (propertyOptionsValues.length === 1) {
					setSelectedPropertyKey(propertyOptionsValues[0].value);
					setSelectedProperty(shop_products[0].property);
				}
			}
		}
	}, []);

	const handleOnErrorGetProductForOrder = useCallback(error => {
		console.log("Error Get Product For Order error: ", error);
	}, []);

	const { data, loading, error } = useGetProductForOrderQuery({
		skip: !params.productId || !userPropertiesIds || userPropertiesIds.length === 0,
		variables: {
			id: params.productId,
			propertyIds: userPropertiesIds
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onCompleted: handleOnCompleteGetProductForOrder,
		onError: handleOnErrorGetProductForOrder
	});

	// A shop product holds no data of its own, so an AI question about it has to target its installed units.
	const { data: installedDevicesData } = useGetInstalledDevicesByDeviceIdQuery({
		skip: !params.productId || !userPropertiesIds.length,
		variables: {
			device_id: params.productId,
			property_ids: userPropertiesIds
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true
	});

	const handleOnCompleteShopProductById = useCallback((data: any) => {
		// console.log("Complete Get Shop Product ById data: ", data);
	}, []);

	const handleOnErrorShopProductById = useCallback(error => {
		console.log("Error Get Shop Product ById Error: ", error);
	}, []);

	const {
		data: shopProductByIdData,
		loading: shopProductByIdLoading,
		error: shopProductByIdError
	} = useGetShopProductByIdQuery({
		skip: !defaultShopProductId,
		variables: {
			id: defaultShopProductId
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onCompleted: handleOnCompleteShopProductById,
		onError: handleOnErrorShopProductById
	});

	const filterByInstalledProductType = useCallback(
		(value?: string) => {
			if (isAdmin) {
				if (value) {
					const path = `${routeNames.ADMIN_PROPERTY_DETAILS.replace(":propertyId", value)}`;

					navigate(path);
				} else {
					navigate(routeNames.ADMIN_PROPERTIES);
				}
			} else {
				if (value) {
					setCategoryFilters([value]);
				} else {
					setCategoryFilters([]);
				}

				navigate(routeNames.ORDER);
			}
		},
		[setCategoryFilters, navigate, isAdmin]
	);

	const handleOnClickAI = useCallback(() => {
		const product = data?.products_by_pk;

		if (product) {
			const shopProducts = product.shop_products ?? [];
			const productName = product.name ?? "";

			if (selectedPropertyKey) {
				// The backend derives the Doctrine partitions from this row, so it has to be the one matching
				// the selected property; the first row would answer about an arbitrary one.
				const shopProductForProperty = shopProducts.find(element => element.property?.id === selectedPropertyKey);

				updateAiChatStatus({
					...aiChatStatusInitialValue,
					chatOrigin: AiChatOrigin.SPECIALTY,
					shop_product_id: shopProductForProperty?.id ?? defaultShopProductId,
					chatTopic: `I understand that you may have a question related to the ${productName} at ${selectedProperty?.name ?? ""}.`,
					selected_properties_ids: [selectedPropertyKey],
					selected_products_ids: product.id ? [product.id] : []
				});
			} else {
				// No single shop_products row to scope to, so send everything the product spans and let the
				// backend union those partitions.
				const installedProducts = installedDevicesData?.products_by_pk?.installed_products ?? [];

				updateAiChatStatus({
					...aiChatStatusInitialValue,
					chatOrigin: AiChatOrigin.SPECIALTY,
					chatTopic: `I understand that you may have a question related to the ${productName} at your properties.`,
					selected_properties_ids: shopProducts.map(element => element.property?.id).filter(Boolean) as string[],
					selected_products_ids: product.id ? [product.id] : [],
					selected_installed_products_ids: installedProducts.map(element => element.id)
				});
			}
		}

		navigate(routeNames.AI_CHAT);
	}, [
		data?.products_by_pk,
		installedDevicesData?.products_by_pk,
		defaultShopProductId,
		selectedPropertyKey,
		selectedProperty,
		updateAiChatStatus,
		navigate
	]);

	const handleOnSelectProperty = useCallback(
		propertyId => {
			setSelectedPropertyKey(propertyId);

			const selectedOption = propertiesOptions.find(option => option.value === propertyId);

			if (selectedOption) {
				setSelectedProperty(selectedOption.property);
			}
		},
		[propertiesOptions]
	);

	const handleOnAddToCart = useCallback(() => {
		if (data?.products_by_pk && selectedProperty && quantity) {
			addToCart(
				data?.products_by_pk as unknown as Products,
				selectedProperty,
				quantity,
				data?.products_by_pk.retail_price
			);

			message.success(`Item added to cart.`);

			navigate(routeNames.ORDER);
		}
	}, [addToCart, data?.products_by_pk, quantity, selectedProperty]);

	const values = useMemo(() => {
		if (params.shopProductId) {
			if (shopProductByIdData?.shop_products_by_pk) {
				const { product, property } = shopProductByIdData?.shop_products_by_pk;

				const {
					name = "",
					description = "",
					product_attachments = [],
					model = "",
					make = "",
					retail_price = "",
					photo_link = ""
				} = product || {};

				return {
					type: product?.product_category,
					name,
					description,
					make,
					model,
					retail_price,
					productImg: photo_link,
					property,
					product_attachments,
					productProperties: []
				};
			}
		} else {
			if (data?.products_by_pk) {
				const {
					name = "",
					description = "",
					product_attachments = [],
					model = "",
					make = "",
					retail_price = "",
					photo_link = "",
					shop_products
				} = data.products_by_pk;

				const productProperties = shop_products.map(element => element.property);

				return {
					type: data.products_by_pk.product_category,
					name,
					description,
					make,
					model,
					retail_price,
					productImg: photo_link,
					property: null,
					product_attachments,
					productProperties
				};
			}
		}

		return {
			type: { label: "", name: "" },
			name: "",
			description: "",
			make: "",
			model: "",
			retail_price: "",
			productImg: "",
			property: null,
			product_attachments: [],
			productProperties: []
		};
	}, [params.shopProductId, data, shopProductByIdData]);

	return (
		<div className="products-details-screen-container">
			{values.name ? (
				<Breadcrumb
					className="products-breadcrumbs"
					items={
						isAdmin
							? [
									{
										title: "Properties",
										onClick: () => filterByInstalledProductType()
									},
									{
										title: values.property?.name,
										onClick: () => filterByInstalledProductType(values.property?.id)
									},
									{
										title: values.name
									}
								]
							: [
									{
										title: "Products",

										onClick: () => filterByInstalledProductType()
									},
									{
										title: values.type?.label,
										onClick: () => filterByInstalledProductType(values.type?.name)
									},
									{
										title: values.name
									}
								]
					}
				/>
			) : null}
			<div className="product-details-container">
				<div className="product-d-row">
					<div className="shop-product-d-image-container">
						{values.productImg ? <img src={values.productImg} className="shop-product-d-image" /> : null}
						<button className="shop-product-d-ai-button" onClick={handleOnClickAI}>
							Ask a question about this
						</button>
					</div>
					<div className="product-descriptions-container">
						<div className="shop-product-details-header">
							<span className="shop-product-details-name">{values.name}</span>
							{loggedUserValue.organization?.display_price ? (
								<span className="shop-product-details-price">{values.retail_price}</span>
							) : null}
						</div>
						<div className="product-d-data-container">
							<span className="product-d-data">{`Make: ${values.make}`}</span>
							<span className="product-d-data">{`Model: ${values.model}`}</span>
						</div>
						<p className="product-d-data description">{values.description}</p>

						{isAdmin ? null : (
							<>
								<Form.Item>
									<Select
										value={selectedPropertyKey || undefined}
										defaultActiveFirstOption={false}
										className="add-product-attachment-input"
										popupClassName="property-popup"
										onChange={(value: string) => handleOnSelectProperty(value)}
										options={propertiesOptions}
										placeholder="Select Property"
										allowClear
										disabled={propertiesOptions.length === 1}
									/>
								</Form.Item>
								<Form.Item>
									<InputNumber
										name="quantity"
										placeholder="Quantity"
										className="add-product-attachment-input"
										value={quantity}
										onChange={value => {
											setQuantity(value);
										}}
									/>
								</Form.Item>
								<button className="shop-product-d-button" onClick={handleOnAddToCart}>
									Add to Cart
								</button>
							</>
						)}
					</div>
				</div>
				<div className="product-d-separator"></div>

				<div className="product-d-attachments">
					{values.product_attachments.map(attachment => (
						<DeviceAttachment
							key={attachment.id}
							//@ts-ignore TODO: come back to handle this
							attachment={attachment}
							onOpenPDF={handleOnOpenPDF}
							onOpenVideo={handleOnOpenVideo}
						/>
					))}
				</div>
			</div>
			<MediaPlayer
				onCancel={() => setFileViewer({ type: null, src: "" })}
				src={fileViewer.src}
				open={!!fileViewer.src && fileViewer.type !== null}
				mediaType={fileViewer.type}
			/>
		</div>
	);
};

export default ShopProductDetails;
